import { useState, useEffect, useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";
import api, { apiErr, calendarCostSeconds } from "../../lib/api";
import { Card, btnPrimary, CostBadge } from "../ui-bits";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { useUploads } from "../../context/UploadContext";
import { toast } from "sonner";
import { CalendarDays, List, Sparkles, Plus, Trash2, Wand2, Loader2, ChevronLeft, ChevronRight, CheckCircle2, X, Youtube, Lightbulb, Clock } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function CalendarPanel({ channel, onCreateFromEntry, onCreateOneClick, onOpenProject }) {
  const { t } = useTranslation();
  const { startCalendarGenerate, registerReload } = useUploads();
  const WD = t("calp.weekdays", { returnObjects: true });
  const MONTHS = t("calp.months", { returnObjects: true });
  const fmtDateLong = (iso) => {
    const d = new Date(iso + "T00:00:00");
    return `${WD[(d.getDay() + 6) % 7]} · ${d.getDate()} ${MONTHS[d.getMonth()].toLowerCase()}`;
  };

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("mes"); // mes | agenda
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });

  const [showGen, setShowGen] = useState(false);
  const [perWeek, setPerWeek] = useState(3);
  const [weeks, setWeeks] = useState(4);
  const [startDate, setStartDate] = useState(todayISO());
  const [genBusy, setGenBusy] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [addDate, setAddDate] = useState(todayISO());
  const [addTitle, setAddTitle] = useState("");
  const [addDescripcion, setAddDescripcion] = useState("");
  const [addAutoCreate, setAddAutoCreate] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [enrichBusy, setEnrichBusy] = useState(false);
  const [ytConnected, setYtConnected] = useState(false);
  const [publishBusy, setPublishBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/channels/${channel.id}/calendar`); setEntries(data); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  }, [channel.id]);

  useEffect(() => { load(); }, [load]);
  // Si el usuario cambia de canal/pestaña mientras la píldora de calendario sigue trabajando,
  // esto refresca la lista en cuanto termina aunque ya no esté esperando la promesa localmente.
  useEffect(() => registerReload(channel.id, load), [channel.id, load, registerReload]);
  useEffect(() => {
    api.get("/youtube/status", { params: { channel_id: channel.id } }).then((r) => setYtConnected(!!r.data.connected)).catch(() => {});
  }, [channel.id]);

  const publish = async (e) => {
    setPublishBusy(e.id);
    try {
      const { data } = await api.post(`/channels/${channel.id}/calendar/${e.id}/publish`);
      if (data.status === "needs_render") {
        toast.warning(t("calp.needsRender"));
      } else {
        toast.success(data.scheduled ? t("calp.scheduledOk") : t("calp.uploadingOk"));
      }
      setTimeout(load, 6000);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setPublishBusy(null); }
  };

  const changeDate = async (e, newDate) => {
    if (!newDate || newDate === e.date) return;
    try {
      await api.patch(`/channels/${channel.id}/calendar/${e.id}`, { date: newDate });
      // El backend limpia auto_create_error al reprogramar (da otra oportunidad a la creación
      // automática) — se refleja aquí para no tener que recargar toda la lista.
      setEntries((prev) => prev.map((x) => x.id === e.id ? { ...x, date: newDate, auto_create_error: null } : x));
      toast.success(t("calp.dateUpdated"));
    } catch (err) { toast.error(apiErr(err)); }
  };

  const toggleAutoCreate = async (e) => {
    const next = !e.auto_create;
    try {
      await api.patch(`/channels/${channel.id}/calendar/${e.id}`, { auto_create: next });
      setEntries((prev) => prev.map((x) => x.id === e.id
        ? { ...x, auto_create: next, auto_create_error: next ? null : x.auto_create_error } : x));
      toast.success(next ? t("calp.autoCreateOn") : t("calp.autoCreateOff"));
    } catch (err) { toast.error(apiErr(err)); }
  };

  const estTitles = Math.max(1, perWeek * weeks);

  const generate = async () => {
    setGenBusy(true);
    try {
      // Vía startCalendarGenerate (UploadContext) en vez de api.post directo: registra la
      // píldora global de arriba (sobrevive a cambios de pestaña, igual que guion/render/
      // miniatura) — antes esta llamada solo tenía el spinner local del botón, invisible en
      // cuanto el usuario se movía de aquí mientras el análisis de competidores + la IA corrían.
      const data = await startCalendarGenerate(channel.id, channel.name, {
        videos_per_week: perWeek, weeks, start_date: startDate,
      });
      toast.success(t("calp.generatedOk", { count: data.created }));
      setShowGen(false);
      await load();
    } catch (e) { if (e?.response?.status !== 402) toast.error(apiErr(e)); }
    finally { setGenBusy(false); }
  };

  const addEntry = async () => {
    if (!addTitle.trim()) { toast.warning(t("calp.writeTitle")); return; }
    setAddBusy(true);
    try {
      await api.post(`/channels/${channel.id}/calendar`, {
        date: addDate, title: addTitle.trim(), descripcion: addDescripcion.trim(), auto_create: addAutoCreate,
      });
      toast.success(t("calp.entryAdded"));
      setShowAdd(false); setAddTitle(""); setAddDescripcion(""); setAddAutoCreate(false);
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setAddBusy(false); }
  };

  const removeEntry = async (e) => {
    if (!window.confirm(t("calp.deleteConfirm"))) return;
    try {
      await api.delete(`/channels/${channel.id}/calendar/${e.id}`);
      setEntries((prev) => prev.filter((x) => x.id !== e.id));
    } catch (err) { toast.error(apiErr(err)); }
  };

  const missingIdea = entries.filter((e) => !e.idea && e.status !== "created").length;
  const enrich = async () => {
    setEnrichBusy(true);
    try {
      const { data } = await api.post(`/channels/${channel.id}/calendar/enrich`);
      toast.success(t("calp.enrichedOk", { count: data.enriched }));
      await load();
    } catch (e) { if (e?.response?.status !== 402) toast.error(apiErr(e)); }
    finally { setEnrichBusy(false); }
  };

  const byDate = entries.reduce((acc, e) => { (acc[e.date] = acc[e.date] || []).push(e); return acc; }, {});

  // ----- Month grid -----
  const firstDow = (new Date(cursor.y, cursor.m, 1).getDay() + 6) % 7; // lunes=0
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const prevMonth = () => setCursor((c) => { const m = c.m - 1; return m < 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m }; });
  const nextMonth = () => setCursor((c) => { const m = c.m + 1; return m > 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m }; });
  const cellISO = (d) => `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  const ytState = (e) => {
    if (e.yt?.status === "scheduled") return "scheduled";
    if (e.yt?.status === "done" || e.youtube_url) return "published";
    if (e.project_id) return e.project_rendered ? "ready" : "in_process";
    return "idea";
  };
  const chipColor = (e) => {
    const s = ytState(e);
    // Distintos colores para "programado en YouTube" (aún no público) vs "ya publicado" — antes
    // ambos compartían el mismo rojo y solo se distinguían leyendo la etiqueta dentro del popover.
    if (s === "scheduled") return "bg-indigo-100 text-indigo-800 hover:bg-indigo-200";
    if (s === "published") return "bg-red-100 text-red-800 hover:bg-red-200";
    if (s === "in_process") return "bg-amber-100 text-amber-800 hover:bg-amber-200";
    if (s === "ready") return "bg-emerald-100 text-emerald-800 hover:bg-emerald-200";
    return "bg-blue-100 text-blue-800 hover:bg-blue-200";
  };
  const StatusLabel = ({ e }) => {
    const s = ytState(e);
    if (s === "scheduled") return <span className="text-xs font-bold text-indigo-700 inline-flex items-center gap-1"><CalendarDays size={12} /> {t("calp.stScheduled")}{e.yt?.publish_at ? ` · ${new Date(e.yt.publish_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}` : ""}</span>;
    if (s === "published") return <span className="text-xs font-bold text-red-700 inline-flex items-center gap-1"><Youtube size={12} /> {t("calp.stPublished")}{e.yt?.at ? ` · ${new Date(e.yt.at).toLocaleDateString(undefined, { dateStyle: "short" })}` : ""}</span>;
    if (s === "in_process") return <span className="text-xs font-bold text-amber-700 inline-flex items-center gap-1"><Clock size={12} /> {t("calp.stInProcess")}</span>;
    if (s === "ready") return <span className="text-xs font-bold text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={12} /> {t("calp.stReady")}</span>;
    return <span className="text-xs font-bold text-blue-600 inline-flex items-center gap-1"><Lightbulb size={12} /> {t("calp.stIdea")}</span>;
  };

  const EntryActions = ({ e }) => {
    const s = ytState(e);
    if (s === "idea") {
      // Fase 6: dos vías desde la misma entrada — el 1-clic con IA (prellenado desde
      // entry.title + entry.idea.angulo) y el form clásico. El 1-clic va primero porque
      // es la vía por defecto que queremos empujar.
      return (
        <div className="flex-1 flex flex-col gap-1.5">
          {onCreateOneClick && (
            <button data-testid={`cal-create-oneclick-${e.id}`} onClick={() => onCreateOneClick(e)}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-br from-brand-400 to-brand-700 hover:brightness-90 text-white text-sm font-bold shadow-sm">
              <Wand2 size={15} /> {t("calp.createOneClick")}
            </button>
          )}
          <button data-testid={`cal-create-${e.id}`} onClick={() => onCreateFromEntry(e)}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-slate-100 text-slate-700 border-2 border-slate-200 hover:border-slate-400 text-sm font-bold">
            <Plus size={15} /> {t("calp.createVideo")}
          </button>
        </div>
      );
    }
    if (s === "in_process") {
      return <button data-testid={`cal-continue-${e.id}`} onClick={() => onOpenProject && onOpenProject(e.project_id)} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500 text-white text-sm font-bold hover:bg-amber-600"><Wand2 size={15} /> {t("calp.finishCreating")}</button>;
    }
    if (s === "ready") {
      if (!ytConnected) return <span className="flex-1 text-xs text-slate-500">{t("calp.connectYtHint")}</span>;
      return <button data-testid={`cal-publish-${e.id}`} onClick={() => publish(e)} disabled={publishBusy === e.id} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-60">
        {publishBusy === e.id ? <Loader2 size={15} className="animate-spin" /> : <Youtube size={15} />} {t("calp.scheduleYt")}
      </button>;
    }
    return <a href={e.youtube_url || e.yt?.url} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white text-red-600 border-2 border-red-200 text-sm font-bold hover:border-red-300"><Youtube size={15} /> {t("calp.viewYt")}</a>;
  };

  const EntryChip = ({ e }) => (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={`cal-entry-${e.id}`}
          className={`w-full text-left text-xs font-semibold px-1.5 py-1 rounded truncate flex items-center gap-1 ${chipColor(e)}`}>
          {(ytState(e) === "scheduled" || ytState(e) === "published") ? <Youtube size={11} className="shrink-0" /> : ytState(e) === "ready" ? <CheckCircle2 size={11} className="shrink-0" /> : ytState(e) === "in_process" ? <Clock size={11} className="shrink-0" /> : null}
          <span className="truncate">{e.title}</span>
          {/* Marca de "se crea solo" para verlo sin abrir cada entrada. */}
          {e.auto_create && ytState(e) === "idea" && (
            <Wand2 size={11} className="shrink-0 ml-auto opacity-70" data-testid={`cal-auto-mark-${e.id}`} />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3 z-50" data-testid={`cal-entry-pop-${e.id}`}>
        <div className="flex items-center justify-between mb-0.5">
          <div className="text-xs font-semibold text-slate-500">{fmtDateLong(e.date)}</div>
          <StatusLabel e={e} />
        </div>
        <div className="text-sm font-bold text-slate-900 leading-snug mb-1.5">{e.title}</div>
        {e.idea?.angulo && <p className="text-xs text-slate-600 line-clamp-3 mb-2">{e.idea.angulo}</p>}
        {ytState(e) !== "published" && ytState(e) !== "scheduled" && (
          <div className="flex items-center gap-2 mb-2.5">
            <span className="text-xs font-semibold text-slate-500">{t("calp.dateLabel")}</span>
            <input data-testid={`cal-date-${e.id}`} type="date" defaultValue={e.date} onChange={(ev) => changeDate(e, ev.target.value)} className="text-xs border-2 border-slate-200 rounded px-2 py-1 outline-none focus:border-blue-500" />
          </div>
        )}
        {/* Crear el vídeo solo, el día antes. Solo tiene sentido en entradas que aún no tienen
            vídeo y cuya fecha está por llegar: si el día antes ya pasó, activarlo no haría nada. */}
        {ytState(e) === "idea" && e.date > todayISO() && (
          <div className="mb-2.5">
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" data-testid={`cal-auto-create-${e.id}`}
                checked={!!e.auto_create} onChange={() => toggleAutoCreate(e)}
                className="w-4 h-4 mt-0.5 accent-amber-500 shrink-0" />
              <span>
                <span className="block text-xs font-bold text-slate-800">{t("calp.autoCreateTitle")}</span>
                <span className="block text-[11px] text-slate-500 leading-snug">{t("calp.autoCreateDesc")}</span>
              </span>
            </label>
            {e.auto_create_error && (
              <p data-testid={`cal-auto-error-${e.id}`} className="mt-1.5 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 leading-snug">
                {t("calp.autoCreateFailed")} {e.auto_create_error}
              </p>
            )}
          </div>
        )}
        <div className="flex gap-2">
          <EntryActions e={e} />
          <button data-testid={`cal-delete-${e.id}`} onClick={() => removeEntry(e)} className="shrink-0 inline-flex items-center justify-center px-2.5 py-2 rounded-lg bg-white text-red-600 border-2 border-red-200 hover:border-red-300"><Trash2 size={16} /></button>
        </div>
      </PopoverContent>
    </Popover>
  );

  const upcoming = [...entries].sort((a, b) => a.date.localeCompare(b.date)).filter((e) => e.date >= todayISO());
  const agendaList = upcoming.length ? upcoming : [...entries].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="space-y-5" data-testid="calendar-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex gap-2 bg-slate-100 p-1.5 rounded-xl w-fit">
          <button data-testid="cal-view-mes" onClick={() => setView("mes")} className={`min-h-[40px] px-4 rounded-lg text-base font-bold flex items-center gap-2 ${view === "mes" ? "bg-white text-brand-700 shadow" : "text-slate-600"}`}><CalendarDays size={18} /> {t("calp.month")}</button>
          <button data-testid="cal-view-agenda" onClick={() => setView("agenda")} className={`min-h-[40px] px-4 rounded-lg text-base font-bold flex items-center gap-2 ${view === "agenda" ? "bg-white text-brand-700 shadow" : "text-slate-600"}`}><List size={18} /> {t("calp.agenda")}</button>
        </div>
        <div className="flex gap-2">
          <button data-testid="cal-add-button" onClick={() => { setShowAdd((v) => !v); setShowGen(false); }} className="min-h-[48px] px-4 sm:px-5 text-base rounded-xl bg-white text-slate-700 border-2 border-slate-300 hover:border-slate-400 font-bold flex items-center justify-center gap-2"><Plus size={20} /> {t("calp.add")}</button>
          <button data-testid="cal-generate-button" onClick={() => { setShowGen((v) => !v); setShowAdd(false); }} className="min-h-[48px] px-4 sm:px-5 text-base font-bold rounded-xl bg-brand-600 text-white hover:bg-brand-700 transition-colors flex items-center justify-center gap-2"><Sparkles size={20} /> {t("calp.generateAi")}</button>
        </div>
      </div>

      {missingIdea > 0 && (
        <div data-testid="cal-enrich-banner" className="flex flex-wrap items-center gap-3 bg-amber-50 border-2 border-amber-200 rounded-xl px-4 py-3">
          <span className="text-base text-amber-900 font-medium flex-1 min-w-[200px]">
            <Trans i18nKey="calp.enrichBanner" values={{ count: missingIdea }} components={{ 0: <b /> }} />
          </span>
          <button data-testid="cal-enrich-button" onClick={enrich} disabled={enrichBusy} className="inline-flex items-center justify-center gap-2 min-h-[48px] px-4 sm:px-5 text-base rounded-xl bg-amber-500 text-white font-bold hover:bg-amber-600 disabled:opacity-60">
            {enrichBusy ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
            {t("calp.completeIdeas")}
            {!enrichBusy && <CostBadge seconds={calendarCostSeconds(missingIdea)} tip={t("calp.enrichTip")} />}
          </button>
        </div>
      )}

      {showGen && (
        <Card className="p-5 border-brand-100" data-testid="cal-generate-form">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-lg font-bold text-slate-900">{t("calp.genFormTitle")}</h4>
            <button onClick={() => setShowGen(false)} className="text-slate-400 hover:text-slate-700"><X size={22} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1.5">{t("calp.videosPerWeek")}</label>
              <div className="flex flex-wrap gap-1.5">
                {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                  <button key={n} data-testid={`cal-perweek-${n}`} onClick={() => setPerWeek(n)} className={`w-10 h-10 rounded-lg font-bold ${perWeek === n ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>{n}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1.5">{t("calp.weeks")}</label>
              <select data-testid="cal-weeks" value={weeks} onChange={(e) => setWeeks(parseInt(e.target.value, 10))} className="w-full border-2 border-slate-300 rounded-lg px-3 py-2.5 text-base outline-none focus:border-brand-600">
                {[1, 2, 3, 4, 6, 8].map((w) => <option key={w} value={w}>{w > 1 ? t("calp.weeksN", { n: w }) : t("calp.weekN", { n: w })}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1.5">{t("calp.from")}</label>
              <input data-testid="cal-startdate" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full border-2 border-slate-300 rounded-lg px-3 py-2.5 text-base outline-none focus:border-brand-600" />
            </div>
          </div>
          <p className="text-sm text-slate-600 mt-3"><Trans i18nKey="calp.willPropose" values={{ count: estTitles }} components={{ 0: <b /> }} /></p>
          <button data-testid="cal-generate-confirm" onClick={generate} disabled={genBusy} className={`${btnPrimary} w-full sm:w-auto mt-3 bg-brand-600 hover:bg-brand-700`}>
            {genBusy ? <Loader2 className="animate-spin" size={20} /> : <Wand2 size={20} />}
            {genBusy ? t("calp.generatingBtn") : t("calp.generateCalendar")}
            {!genBusy && <CostBadge seconds={calendarCostSeconds(estTitles)} tip={t("calp.genTip")} />}
          </button>
        </Card>
      )}

      {showAdd && (
        <Card className="p-5" data-testid="cal-add-form">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="sm:w-44">
                <label className="block text-sm font-bold text-slate-700 mb-1.5">{t("calp.fieldDate")}</label>
                <input data-testid="cal-add-date" type="date" value={addDate} onChange={(e) => setAddDate(e.target.value)} className="w-full border-2 border-slate-300 rounded-lg px-3 py-2.5 text-base outline-none focus:border-blue-600" />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-bold text-slate-700 mb-1.5">{t("calp.fieldTitle")}</label>
                <input data-testid="cal-add-title" value={addTitle} onChange={(e) => setAddTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addEntry()} placeholder={t("calp.titlePlaceholder")} maxLength={120} className="w-full border-2 border-slate-300 rounded-lg px-3 py-2.5 text-base outline-none focus:border-blue-600" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1.5">{t("calp.fieldDescription")}</label>
              <textarea data-testid="cal-add-description" value={addDescripcion} onChange={(e) => setAddDescripcion(e.target.value)} placeholder={t("calp.descriptionPlaceholder")} maxLength={500} rows={3} className="w-full border-2 border-slate-300 rounded-lg px-3 py-2.5 text-base outline-none focus:border-blue-600" />
            </div>
            <div className="flex items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                <input data-testid="cal-add-autocreate" type="checkbox" checked={addAutoCreate} onChange={(e) => setAddAutoCreate(e.target.checked)} className="w-4 h-4" />
                {t("calp.autoCreateTitle")}
              </label>
              <button data-testid="cal-add-confirm" onClick={addEntry} disabled={addBusy} className={btnPrimary}>{addBusy ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />} {t("calp.add")}</button>
            </div>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-brand-600" size={36} /></div>
      ) : entries.length === 0 ? (
        <Card className="p-10 text-center">
          <CalendarDays size={52} className="mx-auto text-brand-600 mb-3" />
          <h3 className="text-xl font-bold text-slate-800 mb-1">{t("calp.emptyTitle")}</h3>
          <p className="text-base text-slate-600">{t("calp.emptyDesc")}</p>
        </Card>
      ) : view === "mes" ? (
        <Card className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3">
            <button data-testid="cal-prev-month" onClick={prevMonth} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"><ChevronLeft size={22} /></button>
            <h4 className="text-lg font-bold text-slate-900" data-testid="cal-month-label">{MONTHS[cursor.m]} {cursor.y}</h4>
            <button data-testid="cal-next-month" onClick={nextMonth} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"><ChevronRight size={22} /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-bold text-slate-400 mb-1">
            {WD.map((d, i) => <div key={i}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (!d) return <div key={`e${i}`} className="min-h-[72px]" />;
              const iso = cellISO(d);
              const list = byDate[iso] || [];
              const isToday = iso === todayISO();
              return (
                <div key={iso} className={`min-h-[72px] rounded-lg border p-1 ${isToday ? "border-brand-300 bg-brand-50/40" : "border-slate-200"}`}>
                  <div className={`text-xs font-bold mb-0.5 ${isToday ? "text-brand-700" : "text-slate-400"}`}>{d}</div>
                  <div className="space-y-0.5">
                    {list.map((e) => <EntryChip key={e.id} e={e} />)}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="cal-agenda">
          {agendaList.map((e) => (
            <Card key={e.id} className="p-3 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center justify-between gap-3 sm:contents">
                <div className="w-28 shrink-0 text-sm font-semibold text-slate-500">{fmtDateLong(e.date)}</div>
                <button data-testid={`cal-delete-${e.id}`} onClick={() => removeEntry(e)} className="shrink-0 text-slate-400 hover:text-red-600 p-1.5 sm:hidden"><Trash2 size={18} /></button>
              </div>
              <div className="min-w-0 sm:flex-1">
                <div className="text-base font-semibold text-slate-900 truncate">{e.title}</div>
                <StatusLabel e={e} />
              </div>
              <div className="flex sm:shrink-0 sm:w-44 sm:justify-end"><EntryActions e={e} /></div>
              <button data-testid={`cal-delete-${e.id}-desktop`} onClick={() => removeEntry(e)} className="hidden sm:block shrink-0 text-slate-400 hover:text-red-600 p-1.5"><Trash2 size={18} /></button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
